export enum MsgContext {
    DEFAULT = 0,
    DEVELOPERS = 4,
    RESERVATIONS = 7,
    HOMEOWNERS = 8,
    BIZDEV = 9
}

/**
 * Mirrors com.jmotto.multi.msg.v2.api.MsgOrigin (Java enum codes).
 * Note: codes 4 and 5 are shared by multiple origins on the backend
 * (VRBO/Airbnb/Wiki = 4, GuestPortal/OwnerPortal = 5). Use the channel
 * label or origin context to disambiguate when rendering.
 */
export enum MsgOrigin {
    DEFAULT = 0,
    ADMIN_INBOX = 1,
    SMS = 2,
    CALL = 3,
    VRBO_API = 4,
    GUEST_PORTAL = 5,
    EMAIL = 6,
    INTERNAL_NOTE = 7
}

export class MsgVO {
    msgId: number;
    conversationId: string;
    context: MsgContext;
    origin: MsgOrigin;
    channel: string;
    senderName: string;
    senderEmail: string;
    senderMobileNum: string;
    recipientEmail: string;
    recipientMobileNum: string;
    subject: string;
    body: string;
    bodyHtml: string;
    createDate: string;
    /** Raw ISO date string (or epoch ms) used for date-group calculation. Not displayed directly. */
    createDateRaw?: string;
    sentBy: string;
    isIncoming: boolean;
    isNote: boolean;
    isSystem: boolean;
    deliveryStatus: string;
    attachmentCount: number;
    mailbox: string;
    label: string;
    expanded: boolean;
}

/**
 * Mirrors com.jmotto.multi.msg.v2.api.NoteRequestAPI.
 * Sender identity is taken from the authenticated user on the server
 * (no sentBy field — do not send one).
 */
export class NoteRequestAPI {
    msgConversationId: string;
    body: string;
    msgContext: number;
    msgSubContext?: number;
    otherEmail?: string;
    otherMobileNum?: string;
    reservationId?: number;
    guestMasterProfileId?: number;
    ownerLeadProfileId?: number;
    quoteId?: number;
}

export class ReservationVO {
    reservationId: number;
    confirmationNumber: string;
    propertyName: string;
    propertyCode: string;
    checkInDate: string;
    checkOutDate: string;
    guestCount: number;
    totalAmount: number;
    status: string;
    paymentStatus: string;
    orderId: number;
    resStatus: 'upcoming' | 'current' | 'past' | 'cancelled';
}

export class QuoteVO {
    quoteId: number;
    quoteNumber: string;
    propertyName: string;
    sentDate: string;
    totalAmount: number;
    status: string;
}

export class OrderVO {
    orderId: number;
    orderNumber: string;
    totalAmount: number;
    status: string;
    createDate: string;
}

export class TaskVO {
    taskId: number;
    title: string;
    status: string;
    priority: number;
    assignedTo: string;
    dueDate: string;
}

export class GuestContactVO {
    contactId: number;
    firstName: string;
    lastName: string;
    emailAddress: string;
    mobileNum: string;
    initials: string;
    isVip: boolean;
    vipStayCount: number;
    isGmpVerified: boolean;
    preferredIsland: string;
    birthday: string;
    memberSince: string;
    lastEmailDate: string;
    lastSmsDate: string;
    lifetimeValue: number;
    stayCount: number;
    gmpId: number;
    labels: string[];
}

export class NavCount {
    mailbox: string;
    count: number;
}

/**
 * One row in the unified-inbox conversation list.
 * Shape mirrors the /api/2.6/msgs/unified payload.
 * Extended with task-commingling fields (isTask, isOverdue, taskId).
 */
export class ConversationSummaryVO {
    conversationId: string;
    lastDate: string;
    lastSenderName: string;
    lastSenderEmail: string;
    lastSubject: string;
    lastBodyPreview: string;
    lastOrigin: number | string;  // number for real messages, 'TASK' sentinel for task rows
    msgCount: number;
    isRead: boolean;
    urgency: string;   // null | 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
    // TODO: backend needs to add contactName to /api/2.6/msgs/unified conversation list response.
    // contactName is the name of the other party (guest/owner/contact), independent of who sent
    // the last message. Without it, outbound threads show "KBM Resorts" as the primary label.
    contactName?: string;
    // TODO: backend needs to add propertyCode and checkInDate to /api/2.6/msgs/unified conversation
    // list response so each row can show e.g. "HKK-334 • Aug 2–9" as a secondary line.
    propertyCode?: string;
    checkInDate?: string;
    checkOutDate?: string;
    // Task-commingling fields — only set when isTask=true
    isTask?: boolean;
    isOverdue?: boolean;
    taskId?: number;
    taskStatus?: number;
    taskStatusLabel?: string;
    taskReservationId?: number;
}

/**
 * Response shape from GET /api/2.6/msgs/unified/due-tasks.
 */
export class DueTaskVO {
    taskId: number;
    name: string;
    description: string;
    assignedToUserName: string;
    dateToFollowup: string;
    dateDue: string;
    status: number;
    statusLabel: string;
    msgConversationId: string;   // null when task has no linked conversation
    reservationId: number;
    guestName: string;
    overdue: boolean;
}

export class AttachmentVO {
    fileName: string;
    contentType: string;
    sizeBytes: number;
    url: string;
    available: boolean;
}

export const MOCK_CONTACT: GuestContactVO = {
    contactId: 8821,
    firstName: 'Jane',
    lastName: 'Smith',
    emailAddress: 'jane.smith@example.com',
    mobileNum: '+1 (808) 555-0199',
    initials: 'JS',
    isVip: true,
    vipStayCount: 3,
    isGmpVerified: true,
    preferredIsland: 'Maui',
    birthday: 'April 22',
    memberSince: '2021-11-02',
    lastEmailDate: '2h ago',
    lastSmsDate: '3h ago',
    lifetimeValue: 8305,
    stayCount: 3,
    gmpId: 8821,
    labels: ['VIP', 'Returning Guest', 'Late Check-in']
};

export const MOCK_MESSAGES: MsgVO[] = [
    {
        msgId: 1,
        conversationId: 'conv-001',
        context: MsgContext.RESERVATIONS,
        origin: MsgOrigin.EMAIL,
        channel: 'Email',
        senderName: 'KBM · Emily',
        senderEmail: 'emily@kbmhawaii.com',
        senderMobileNum: null,
        recipientEmail: 'jane.smith@example.com',
        recipientMobileNum: null,
        subject: 'Re: Late check-in request',
        body: 'Hi Jane! Confirmed — your 7:30pm arrival works great. Lockbox code is 4482. Mahalo!',
        bodyHtml: null,
        createDate: 'Today 2:14 PM',
        sentBy: 'Emily',
        isIncoming: false,
        isNote: false,
        isSystem: false,
        deliveryStatus: 'Opened',
        attachmentCount: 0,
        mailbox: 'Sent',
        label: null,
        expanded: false
    },
    {
        msgId: 2,
        conversationId: 'conv-001',
        context: MsgContext.RESERVATIONS,
        origin: MsgOrigin.SMS,
        channel: 'SMS',
        senderName: 'Jane',
        senderEmail: null,
        senderMobileNum: '+18085550199',
        recipientEmail: null,
        recipientMobileNum: null,
        subject: null,
        body: "Hi! Flight's been delayed — can I still get in at 7:30pm instead of 4? Thanks!",
        bodyHtml: null,
        createDate: 'Today 2:11 PM',
        sentBy: 'Jane',
        isIncoming: true,
        isNote: false,
        isSystem: false,
        deliveryStatus: null,
        attachmentCount: 0,
        mailbox: 'Inbox',
        label: null,
        expanded: false
    },
    {
        msgId: 3,
        conversationId: 'conv-001',
        context: MsgContext.RESERVATIONS,
        origin: MsgOrigin.INTERNAL_NOTE,
        channel: 'Note',
        senderName: 'Lorry',
        senderEmail: null,
        senderMobileNum: null,
        recipientEmail: null,
        recipientMobileNum: null,
        subject: null,
        body: 'Flagged as VIP — comp welcome basket, white-glove check-in (3rd stay).',
        bodyHtml: null,
        createDate: 'Today 10:02 AM',
        sentBy: 'Lorry',
        isIncoming: false,
        isNote: true,
        isSystem: false,
        deliveryStatus: null,
        attachmentCount: 0,
        mailbox: null,
        label: null,
        expanded: false
    },
    {
        msgId: 4,
        conversationId: 'conv-001',
        context: MsgContext.RESERVATIONS,
        origin: MsgOrigin.VRBO_API,
        channel: 'VRBO API',
        senderName: 'VRBO API',
        senderEmail: null,
        senderMobileNum: null,
        recipientEmail: null,
        recipientMobileNum: null,
        subject: null,
        body: 'Booking confirmed · Kahana Falls #204 · Oct 14–18 · 4 guests · $2,845.00',
        bodyHtml: null,
        createDate: 'Oct 3, 4:48 PM',
        sentBy: null,
        isIncoming: false,
        isNote: false,
        isSystem: true,
        deliveryStatus: null,
        attachmentCount: 0,
        mailbox: null,
        label: null,
        expanded: false
    }
];
